import { useReducer } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Shirt, Package, Image, MoreVertical, Pencil, Trash2, Loader2 } from 'lucide-react';
import { usePhotostudioElements } from '@/hooks/usePhotostudioElements';
import type { IPhotostudioElement, ElementType } from '@/@types/photostudio';
import { ElementUploadDialog } from '@/components/photostudio/ElementUploadDialog';
import { ElementEditDialog } from '@/components/photostudio/ElementEditDialog';
import { ElementDeleteDialog } from '@/components/photostudio/ElementDeleteDialog';

const ELEMENT_TYPES: { type: ElementType; label: string; icon: typeof Shirt }[] = [
  { type: 'outfit', label: 'Outfits', icon: Shirt },
  { type: 'prop', label: 'Props', icon: Package },
  { type: 'scene', label: 'Scenes', icon: Image },
];

interface IElementCardProps {
  element: IPhotostudioElement;
  onEdit: () => void;
  onDelete: () => void;
}

function ElementCard({ element, onEdit, onDelete }: IElementCardProps) {
  const Icon = element.type === 'outfit' ? Shirt : element.type === 'prop' ? Package : Image;

  return (
    <Card className="group overflow-hidden">
      <div className="relative aspect-square bg-muted">
        {element.imageUrl ? (
          <img src={element.imageUrl} alt={element.name} className="size-full object-cover" />
        ) : (
          <div className="flex size-full items-center justify-center">
            <Icon className="size-12 text-muted-foreground/50" />
          </div>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              size="icon"
              className="absolute right-2 top-2 size-8 opacity-0 transition-opacity group-hover:opacity-100"
            >
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="mr-2 size-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={onDelete}>
              <Trash2 className="mr-2 size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="p-3">
        <p className="truncate font-medium">{element.name}</p>
        {element.description ? (
          <p className="truncate text-sm text-muted-foreground">{element.description}</p>
        ) : (
          <p className="flex items-center gap-1 text-sm text-muted-foreground/60 italic">
            <Loader2 className="size-3 animate-spin" />
            Generating description...
          </p>
        )}
      </div>
    </Card>
  );
}

interface IElementsPageState {
  activeTab: ElementType;
  dialog: 'none' | 'upload' | 'edit' | 'delete';
  selectedElement: IPhotostudioElement | null;
}

type ElementsPageAction =
  | { type: 'SET_TAB'; tab: ElementType }
  | { type: 'OPEN_UPLOAD' }
  | { type: 'OPEN_EDIT'; element: IPhotostudioElement }
  | { type: 'OPEN_DELETE'; element: IPhotostudioElement }
  | { type: 'CLOSE_DIALOG' };

function elementsReducer(
  state: IElementsPageState,
  action: ElementsPageAction
): IElementsPageState {
  switch (action.type) {
    case 'SET_TAB':
      return { ...state, activeTab: action.tab };
    case 'OPEN_UPLOAD':
      return { ...state, dialog: 'upload' };
    case 'OPEN_EDIT':
      return { ...state, dialog: 'edit', selectedElement: action.element };
    case 'OPEN_DELETE':
      return { ...state, dialog: 'delete', selectedElement: action.element };
    case 'CLOSE_DIALOG':
      return { ...state, dialog: 'none', selectedElement: null };
  }
}

const INITIAL_ELEMENTS_STATE: IElementsPageState = {
  activeTab: 'outfit',
  dialog: 'none',
  selectedElement: null,
};

export function PhotostudioElementsPage() {
  const {
    outfits,
    props,
    scenes,
    isLoading,
    error,
    uploadAndCreateElement,
    updateElement,
    deleteElement,
  } = usePhotostudioElements();

  const [state, dispatch] = useReducer(elementsReducer, INITIAL_ELEMENTS_STATE);

  const getElements = (type: ElementType): IPhotostudioElement[] => {
    switch (type) {
      case 'outfit':
        return outfits;
      case 'prop':
        return props;
      case 'scene':
        return scenes;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Element Library</h2>
          <p className="text-sm text-muted-foreground">
            Reusable outfits, props, and scenes for your images
          </p>
        </div>
        <Button onClick={() => dispatch({ type: 'OPEN_UPLOAD' })}>
          <Plus className="mr-2 size-4" />
          Add Element
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}

      <Tabs
        value={state.activeTab}
        onValueChange={v => dispatch({ type: 'SET_TAB', tab: v as ElementType })}
      >
        <TabsList>
          {ELEMENT_TYPES.map(({ type, label, icon: Icon }) => (
            <TabsTrigger key={type} value={type} className="gap-2">
              <Icon className="size-4" />
              {label}
              <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-xs">
                {getElements(type).length}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {ELEMENT_TYPES.map(({ type, label, icon: Icon }) => (
          <TabsContent key={type} value={type} className="mt-4">
            {getElements(type).length === 0 ? (
              <Card className="flex flex-col items-center justify-center py-12">
                <Icon className="mb-4 size-16 text-muted-foreground/50" />
                <p className="text-lg font-medium">No {label.toLowerCase()} yet</p>
                <p className="mb-4 text-sm text-muted-foreground">
                  Add {label.toLowerCase()} to use as references in your images
                </p>
                <Button onClick={() => dispatch({ type: 'OPEN_UPLOAD' })}>
                  <Plus className="mr-2 size-4" />
                  Add {label.slice(0, -1)}
                </Button>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {getElements(type).map(element => (
                  <ElementCard
                    key={element._id}
                    element={element}
                    onEdit={() => dispatch({ type: 'OPEN_EDIT', element })}
                    onDelete={() => dispatch({ type: 'OPEN_DELETE', element })}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <ElementUploadDialog
        open={state.dialog === 'upload'}
        onOpenChange={open => !open && dispatch({ type: 'CLOSE_DIALOG' })}
        activeTab={state.activeTab}
        onSubmit={(name, file, description) =>
          uploadAndCreateElement(state.activeTab, name, file, description)
        }
      />

      <ElementEditDialog
        open={state.dialog === 'edit'}
        onOpenChange={open => !open && dispatch({ type: 'CLOSE_DIALOG' })}
        element={state.selectedElement}
        onSubmit={updateElement}
      />

      <ElementDeleteDialog
        open={state.dialog === 'delete'}
        onOpenChange={open => !open && dispatch({ type: 'CLOSE_DIALOG' })}
        element={state.selectedElement}
        onConfirm={deleteElement}
      />
    </div>
  );
}
